import { useQuery } from '@tanstack/react-query'
import { Temporal } from 'temporal-polyfill'
import { fetchApiWithToken } from '~/api/utils'
import type { ApplicationsKpiKeyMap } from '~/domains/applications/types'
import type { OffersKpiKeyMap } from '~/domains/offers/types'

export type KpiKeyMap = ApplicationsKpiKeyMap & OffersKpiKeyMap

export type KpiKey = keyof KpiKeyMap

export type KpiPeriod = {
  from: Temporal.Instant
  to: Temporal.Instant
}

export const getKpiPeriods = (
  today: Temporal.PlainDate
): {
  current: KpiPeriod
  previous: KpiPeriod
} => {
  const timeZone = Temporal.Now.timeZoneId()
  const currentTo = today.add({ days: 1 }).toZonedDateTime(timeZone)
  const currentFrom = today.subtract({ days: 6 }).toZonedDateTime(timeZone)
  const previousFrom = currentFrom.subtract({ days: 7 })

  return {
    current: { from: currentFrom.toInstant(), to: currentTo.toInstant() },
    previous: { from: previousFrom.toInstant(), to: currentFrom.toInstant() }
  }
}

export const useKpis = <K extends KpiKey>(
  keys: readonly K[],
  period: KpiPeriod
) => {
  const from = period.from.toString()
  const to = period.to.toString()

  return useQuery<Pick<KpiKeyMap, K>>({
    queryKey: ['kpis', from, to, [...keys].sort()],
    queryFn: ({ signal }) => {
      const searchParams = new URLSearchParams()
      for (const key of keys) {
        searchParams.append('keys[]', key)
      }
      searchParams.set('from', from)
      searchParams.set('to', to)

      return fetchApiWithToken<Pick<KpiKeyMap, K>>({
        method: 'get',
        url: `/v1/kpis?${searchParams.toString()}`,
        signal
      })
    },
    staleTime: 60_000
  })
}
